import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from './Card.jsx';
import { CARDS, PLAYER, ENERGY } from './cards.jsx';
import { useCombat } from './combat.jsx';
import { sfx } from './sfx.js';
import { bio, socials, email } from './roomData.js';
import operatorSprite from '../assets/operator.svg';
import './lair.css';
import './combat.css';

const RELICS = [
  { icon: '☕', name: 'Caffeine Idol', note: 'Focus never fades.' },
  { icon: '⎇', name: 'The Git Charm', note: 'Undo any mistake.' },
  { icon: '★', name: 'Resolve', note: 'Ship it anyway.' },
];

const HealthBar = ({ hp, max, side }) => {
  const pct = max > 0 ? Math.max(0, Math.round((hp / max) * 100)) : 0;
  return (
    <div className={`healthBar side-${side}`}>
      <div className="healthFill" style={{ width: `${pct}%` }} />
      <span className="healthText">
        {hp} / {max}
      </span>
    </div>
  );
};

const CombatScreen = () => {
  const navigate = useNavigate();
  const { monsterData, bgUrl, hp, maxHp, hpPct, kills, isEnabled, toggleCard, strike, advance } =
    useCombat();
  const [playingId, setPlayingId] = useState(null);
  const [hit, setHit] = useState(null);
  const [shake, setShake] = useState(false);
  const [modal, setModal] = useState(null);
  const [celebrating, setCelebrating] = useState(false);
  const [lunging, setLunging] = useState(false);
  const [muted, setMutedState] = useState(sfx.isMuted());

  const deck = CARDS.filter((c) => isEnabled(c.id));
  const n = deck.length;
  const bossDmgClass = celebrating ? '' : hpPct <= 33 ? 'dmg2' : hpPct <= 66 ? 'dmg1' : '';

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') setModal(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const toggleMute = () => {
    const next = !muted;
    sfx.setMuted(next);
    setMutedState(next);
    if (!next) sfx.unlock();
  };

  const handlePlay = (card) => {
    if (playingId) return;
    sfx.unlock();
    const willKill = hp - card.damage <= 0;
    setPlayingId(card.id);
    setLunging(true);
    window.setTimeout(() => setLunging(false), 520);
    sfx.whoosh();

    window.setTimeout(() => {
      strike(card);
      setHit({ dmg: card.damage, t: Date.now() });
      setShake(true);
      sfx.thud();
      window.setTimeout(() => setShake(false), 380);
      window.setTimeout(() => setHit(null), 950);
      if (willKill) {
        setCelebrating(true);
        window.setTimeout(() => sfx.victory(), 240);
      }
    }, 430);

    window.setTimeout(
      () => {
        navigate(`/lair/${card.id}`);
        if (willKill) advance(); // prepare the next foe for when they retreat
      },
      willKill ? 1750 : 920
    );
  };

  return (
    <div className={`arena ${shake ? 'is-shaking' : ''}`}>
      <h1 className="sr-only">CJ Stancin — portfolio, played as a roguelike deck-builder</h1>
      <div className="arenaBackdrop" aria-hidden="true">
        <img className="arenaScene" src={bgUrl} alt="" />
        <div className="arenaFog" />
      </div>

      {hit ? <div className="impactFlash" key={hit.t} aria-hidden="true" /> : null}

      {/* top HUD */}
      <header className="combatHud">
        <div className="runTag">
          THE LAIR · FLOOR 7 <span className="runKills">⚔ {kills} FELLED</span>
        </div>
        <div className="hudBtns">
          <button className="hudChip iconChip" onClick={toggleMute} aria-pressed={muted} title={muted ? 'Unmute' : 'Mute'}>
            {muted ? '🔇' : '🔊'}
          </button>
          <button className="hudChip" onClick={() => setModal('spellbook')}>▦ Spellbook</button>
          <button className="hudChip" onClick={() => setModal('deck')}>▤ Deck · {n}</button>
        </div>
      </header>

      <main className="arenaMain">
        {/* left rail — operator */}
        <aside className="sideRail operatorPanel">
          <div className="railTitle">The Operator</div>
          <button className="opPortraitBtn" onClick={() => setModal('profile')} title="View operator profile">
            <img src={PLAYER.avatar} alt={PLAYER.name} />
            <span className="opHint">▸ Profile</span>
          </button>
          <div className="railName">{PLAYER.name}</div>
          <HealthBar hp={PLAYER.maxHp} max={PLAYER.maxHp} side="player" />
          <div className="energyRow">
            <div className="energyOrb" title={ENERGY.note}>
              <span className="energyVal">{ENERGY.value}</span>
            </div>
            <span className="energyCaption">Energy · jobs worked</span>
          </div>
          <div className="relicHeading">Relics</div>
          <ul className="relicList">
            {RELICS.map((r) => (
              <li key={r.name} title={r.note}>
                <span className="relicGlyph">{r.icon}</span> {r.name}
              </li>
            ))}
          </ul>
        </aside>

        {/* battlefield — hero vs monster */}
        <div className="battlefield">
          <div className={`combatant hero ${lunging ? 'is-attacking' : ''}`}>
            <div className="combatantArt heroArtWrap">
              <img className="heroArt" src={operatorSprite} alt="" aria-hidden="true" />
            </div>
            <span className="combatantName heroName">{PLAYER.name}</span>
            <span className="combatantSub heroSub">The Operator</span>
          </div>

          <div
            className={`combatant boss ${bossDmgClass} ${shake ? 'is-hit' : ''} ${
              celebrating ? 'is-dead is-shattering' : ''
            }`}
          >
            <div className="intentBubble">
              <span className="intentIcon">🛡</span> {monsterData.intent}
            </div>
            <div className="combatantArt bossArtWrap">
              <img className="bossArt" src={monsterData.sprite} alt="" aria-hidden="true" />
              {celebrating && (
                <div className="shards" aria-hidden="true">
                  {Array.from({ length: 9 }).map((_, i) => {
                    const ang = (i / 9) * Math.PI * 2;
                    const dist = 70 + (i % 3) * 30;
                    return (
                      <span
                        key={i}
                        className="shard"
                        style={{
                          '--x': `${Math.cos(ang) * dist}px`,
                          '--y': `${Math.sin(ang) * dist + 50}px`,
                          '--r': `${(i % 2 ? 1 : -1) * 200}deg`,
                          animationDelay: `${i * 18}ms`,
                        }}
                      />
                    );
                  })}
                </div>
              )}
              {hit && <div className="dmgNumber">-{hit.dmg}</div>}
            </div>
            <HealthBar hp={hp} max={maxHp} side="boss" />
            <span className="combatantName bossName">{monsterData.name}</span>
            <span className="combatantSub">{monsterData.subtitle}</span>
          </div>
        </div>

        {/* right rail — the foe */}
        <aside className="sideRail foePanel">
          <div className="railTitle">The Foe</div>
          <div className="foeName">{monsterData.name}</div>
          <div className="foeSub">{monsterData.subtitle}</div>
          <div className="foeIntent">
            <span className="intentIcon">🛡</span> Intent · {monsterData.intent}
          </div>
          <div className="foeStat">
            <span>Vitality</span>
            <strong>
              {hp} / {maxHp}
            </strong>
          </div>
          <div className="foeStat">
            <span>Foes felled</span>
            <strong>{kills}</strong>
          </div>
          <p className="foeLore">
            Strike it down. Another always rises from the lair — a new shape, a new dark.
          </p>
        </aside>
      </main>

      {/* the fanned attack hand */}
      <div className="hand">
        {deck.map((card, i) => {
          const offset = i - (n - 1) / 2;
          const rot = offset * 6;
          const ty = Math.abs(offset) * Math.abs(offset) * 9;
          return (
            <div
              key={card.id}
              className={`fanSlot ${playingId === card.id ? 'is-playing' : ''} ${
                playingId && playingId !== card.id ? 'is-receding' : ''
              }`}
              style={{ '--rot': `${rot}deg`, '--ty': `${ty}px`, zIndex: 10 + i }}
              onMouseEnter={() => {
                sfx.unlock();
                sfx.tick();
              }}
            >
              <Card card={card} variant="full" index={i} onSelect={handlePlay} />
            </div>
          );
        })}
      </div>

      <p className="handHint">Strike the {monsterData.name} — each blow chips its vitality. Drop it to enter beyond.</p>

      {/* monster-felled celebration toast */}
      {celebrating && (
        <div className="felledToast" role="status">
          <span className="felledStar">★</span>
          <span className="felledTitle">{monsterData.name} FELLED!</span>
          <span className="felledSub">A new foe stirs beyond the door…</span>
        </div>
      )}

      {/* profile modal */}
      {modal === 'profile' && (
        <div className="overlay" role="dialog" aria-modal="true" aria-label="Operator profile" onClick={() => setModal(null)}>
          <div className="profileCard" onClick={(e) => e.stopPropagation()}>
            <div className="profileTop">
              <span className="profileAvatar">
                <img src={PLAYER.avatar} alt={PLAYER.name} />
              </span>
              <div>
                <h2>{PLAYER.name}</h2>
                <p className="profileTitle">{PLAYER.title} · Robotics · SWE · Automation</p>
              </div>
            </div>
            <p className="profileBio">{bio}</p>
            <div className="profileLinks">
              {socials.map((s) => (
                <a key={s.label} href={s.href} target="_blank" rel="noopener noreferrer">
                  {s.label} <span>@{s.handle}</span>
                </a>
              ))}
              <a href={`mailto:${email}`}>
                Email <span>{email}</span>
              </a>
            </div>
            <button className="overlayClose" onClick={() => setModal(null)}>Close</button>
          </div>
        </div>
      )}

      {/* deck overlay (tiles + section on/off toggles) */}
      {modal === 'deck' && (
        <div className="overlay" role="dialog" aria-modal="true" aria-label="Your deck" onClick={() => setModal(null)}>
          <div className="deckView" onClick={(e) => e.stopPropagation()}>
            <h2 className="deckTitle">▤ Your Deck</h2>
            <p className="deckMeta">All attacks · toggle rooms on/off · ⚔ {kills} felled</p>
            <div className="deckGrid">
              {CARDS.map((card, i) => {
                const on = isEnabled(card.id);
                return (
                  <div className={`dealSlot ${on ? '' : 'is-off'}`} style={{ '--i': i }} key={card.id}>
                    <button
                      className={`deckToggle ${on ? 'on' : ''}`}
                      role="switch"
                      aria-checked={on}
                      aria-label={`${on ? 'Disable' : 'Enable'} ${card.section}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleCard(card.id);
                      }}
                    >
                      <span className="deckKnob" />
                    </button>
                    <Card
                      card={card}
                      variant="tile"
                      index={i}
                      onSelect={
                        on
                          ? (c) => {
                              setModal(null);
                              handlePlay(c);
                            }
                          : undefined
                      }
                    />
                  </div>
                );
              })}
            </div>
            <button className="overlayClose" onClick={() => setModal(null)}>Done</button>
          </div>
        </div>
      )}

      {/* spellbook — full-card gallery */}
      {modal === 'spellbook' && (
        <div className="overlay" role="dialog" aria-modal="true" aria-label="Spellbook" onClick={() => setModal(null)}>
          <div className="spellbook" onClick={(e) => e.stopPropagation()}>
            <h2 className="deckTitle">▦ Spellbook — The Operator&rsquo;s Deck</h2>
            <p className="deckMeta">Every card, in full. Click one to strike.</p>
            <div className="spellGrid">
              {deck.map((card, i) => (
                <div className="spellEntry dealSlot" style={{ '--i': i }} key={card.id}>
                  <Card
                    card={card}
                    variant="full"
                    index={i}
                    onSelect={(c) => {
                      setModal(null);
                      handlePlay(c);
                    }}
                  />
                  <button
                    className="spellCta"
                    onClick={() => {
                      setModal(null);
                      handlePlay(card);
                    }}
                  >
                    Deal {card.damage} → {card.section}
                  </button>
                </div>
              ))}
            </div>
            <button className="overlayClose" onClick={() => setModal(null)}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CombatScreen;
